/**
 * Prefer usable reporting docs when a non-legacy empty stub
 * (e.g. Sheet import with all-NA charts) overshadows a legacy doc that still has scores.
 */

export type ReportingDocCandidate = {
  id: string;
  data: Record<string, unknown>;
};

const toFiniteNumber = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Higher = more useful for dashboard/export aggregates. */
export function submissionReportingContentScore(data: Record<string, unknown> | null | undefined): number {
  if (!data || typeof data !== 'object') return 0;
  const metrics = (data.metrics as Record<string, unknown> | undefined) ?? undefined;
  const monthlyTotal = toFiniteNumber(metrics?.monthly_total);
  const monthlyScore = toFiniteNumber(metrics?.monthly_score);
  const monthlyPct = toFiniteNumber(metrics?.monthly_pct);
  if (monthlyTotal > 0 || monthlyScore > 0 || monthlyPct > 0) {
    return monthlyTotal * 1_000 + monthlyScore * 10 + monthlyPct;
  }

  const charts = Array.isArray(data.charts) ? data.charts : [];
  let scoredCells = 0;
  for (const chart of charts) {
    if (!chart || typeof chart !== 'object') continue;
    const answers = (chart as { answers?: unknown }).answers;
    if (!Array.isArray(answers)) continue;
    for (const cell of answers) {
      const s = String(cell ?? '').trim();
      if (s !== '' && s.toUpperCase() !== 'NA') scoredCells += 1;
    }
  }
  return scoredCells;
}

export function isCrossAuditReportingDoc(id: string, data: Record<string, unknown>): boolean {
  if (typeof id === 'string' && id.startsWith('CROSS_AUDIT_NURSING')) return true;
  if (data.crossAudit === true) return true;
  if (data.type === 'cross_audit') return true;
  return false;
}

function pickHighestContent(docs: readonly ReportingDocCandidate[]): ReportingDocCandidate | null {
  if (docs.length === 0) return null;
  let best = docs[0]!;
  let bestScore = submissionReportingContentScore(best.data);
  for (let i = 1; i < docs.length; i += 1) {
    const candidate = docs[i]!;
    const score = submissionReportingContentScore(candidate.data);
    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  }
  return best;
}

/**
 * Prefer non-legacy docs with real metrics. If the only non-legacy parent is empty (score 0)
 * but a legacy parent has content, keep that legacy parent so dashboard/export show real data.
 * Cross-audit docs: still exclude legacy.
 */
export function filterReportingDocsPreferringUsableContent(
  docs: readonly ReportingDocCandidate[],
): ReportingDocCandidate[] {
  const crossAudit: ReportingDocCandidate[] = [];
  const parents: ReportingDocCandidate[] = [];

  for (const doc of docs) {
    if (isCrossAuditReportingDoc(doc.id, doc.data)) {
      if (doc.data.legacy !== true) crossAudit.push(doc);
      continue;
    }
    parents.push(doc);
  }

  const nonLegacyParents = parents.filter((d) => d.data.legacy !== true);
  const bestNonLegacy = pickHighestContent(nonLegacyParents);
  if (bestNonLegacy && submissionReportingContentScore(bestNonLegacy.data) > 0) {
    return [...nonLegacyParents, ...crossAudit];
  }

  const bestAny = pickHighestContent(parents);
  if (bestAny && submissionReportingContentScore(bestAny.data) > 0) {
    return [bestAny, ...crossAudit];
  }

  return [...nonLegacyParents, ...crossAudit];
}
